// ScoreSummaryReport.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const ScoreSummaryReport = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  
  useEffect(() => {
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    if (selectedFiscalYear) {
      fetchReportData();
    }
  }, [selectedFiscalYear]);

  const fetchFiscalYears = async () => {
    try {
      const response = await api.get('/fiscal-years');
      setFiscalYears(response.data);
      const activeYear = response.data.find(year => year.is_active);
      if (activeYear) {
        setSelectedFiscalYear(activeYear.id.toString());
      }
    } catch (error) {
      console.error('Error fetching fiscal years:', error);
      Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลปีงบประมาณได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = async () => {
    setReportData([]);
    setSummary(null);
    setLoading(true);
    try {
      const response = await api.get(`/reports/score-summary/${selectedFiscalYear}`);
      setReportData(response.data.schools);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching report data:', error);
      Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลรายงานได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    try {
      const fiscalYear = fiscalYears.find(fy => fy.id.toString() === selectedFiscalYear);
      
      const exportData = reportData.map((school, index) => {
        return {
            'ลำดับ': index + 1,
            'รหัสโรงเรียน': school.school_code,
            'ชื่อโรงเรียน': school.school_name,
            'ผู้อำนวยการ': school.director || '',
            
            'IIT: T1 (%)': school.iit_scores?.indicator_1 ? (school.iit_scores.indicator_1 / 6 * 100).toFixed(2) : '0.00',
            'IIT: T2 (%)': school.iit_scores?.indicator_2 ? (school.iit_scores.indicator_2 / 6 * 100).toFixed(2) : '0.00',
            'IIT: T3 (%)': school.iit_scores?.indicator_3 ? (school.iit_scores.indicator_3 / 6 * 100).toFixed(2) : '0.00',
            'IIT: T4 (%)': school.iit_scores?.indicator_4 ? (school.iit_scores.indicator_4 / 6 * 100).toFixed(2) : '0.00',
            'IIT: T5 (%)': school.iit_scores?.indicator_5 ? (school.iit_scores.indicator_5 / 6 * 100).toFixed(2) : '0.00',
            'รวม IIT (30)': school.iit_score?.toFixed(2) || '0.00',
            
            'EIT1: T6 (%)': school.eit1_scores?.indicator_6 ? (school.eit1_scores.indicator_6 / 4 * 100).toFixed(2) : '0.00',
            'EIT1: T7 (%)': school.eit1_scores?.indicator_7 ? (school.eit1_scores.indicator_7 / 4 * 100).toFixed(2) : '0.00',
            'EIT1: T8 (%)': school.eit1_scores?.indicator_8 ? (school.eit1_scores.indicator_8 / 7 * 100).toFixed(2) : '0.00',
            'รวม EIT1 (15)': school.eit1_score?.toFixed(2) || '0.00',
            
            'EIT2: T6 (%)': school.eit2_scores?.indicator_6 ? (school.eit2_scores.indicator_6 / 4 * 100).toFixed(2) : '0.00',
            'EIT2: T7 (%)': school.eit2_scores?.indicator_7 ? (school.eit2_scores.indicator_7 / 4 * 100).toFixed(2) : '0.00',
            'EIT2: T8 (%)': school.eit2_scores?.indicator_8 ? (school.eit2_scores.indicator_8 / 7 * 100).toFixed(2) : '0.00',
            'รวม EIT2 (15)': school.eit2_score?.toFixed(2) || '0.00',
            
            'OIT 9.1 (%)': school.oit_sub_scores?.s9_1 ? (school.oit_sub_scores.s9_1 / 4 * 100).toFixed(2) : '0.00',
            'OIT 9.2 (%)': school.oit_sub_scores?.s9_2 ? (school.oit_sub_scores.s9_2 / 4 * 100).toFixed(2) : '0.00',
            'OIT 9.3 (%)': school.oit_sub_scores?.s9_3 ? (school.oit_sub_scores.s9_3 / 4 * 100).toFixed(2) : '0.00',
            'OIT 9.4 (%)': school.oit_sub_scores?.s9_4 ? (school.oit_sub_scores.s9_4 / 4 * 100).toFixed(2) : '0.00',
            'OIT 9.5 (%)': school.oit_sub_scores?.s9_5 ? (school.oit_sub_scores.s9_5 / 4 * 100).toFixed(2) : '0.00',
            'OIT 10.1 (%)': school.oit_sub_scores?.s10_1 ? (school.oit_sub_scores.s10_1 / 10 * 100).toFixed(2) : '0.00',
            'OIT 10.2 (%)': school.oit_sub_scores?.s10_2 ? (school.oit_sub_scores.s10_2 / 10 * 100).toFixed(2) : '0.00',
            'รวม OIT (40)': school.oit_score?.toFixed(2) || '0.00',
            
            'คะแนนรวม (100)': school.total_score?.toFixed(2) || '0.00',
            'ระดับผลการประเมิน': school.rating || ''
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ประมวลผลคะแนน');

      ws['!cols'] = [
        { wch: 8 }, { wch: 15 }, { wch: 40 }, { wch: 25 },
        ...Array(21).fill({ wch: 15 }),
        { wch: 20 }
      ];

      const fileName = `รายงานประมวลผลคะแนน_ITA_${fiscalYear?.year || selectedFiscalYear}.xlsx`;
      XLSX.writeFile(wb, fileName);
      Swal.fire('สำเร็จ', 'ส่งออกรายงานเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถส่งออกรายงานได้', 'error');
    }
  };

  const getRatingBadgeClass = (rating) => {
    switch (rating) {
      case 'ผ่านดีเยี่ยม': return 'bg-success';
      case 'ผ่านดี': return 'bg-info';
      case 'ผ่าน': return 'bg-primary';
      case 'ต้องปรับปรุง': return 'bg-warning';
      case 'ต้องปรับปรุงโดยด่วน': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  if (loading && !reportData.length) {
    return (
      <div className="wrapper">
        <Header user={user} title="รายงานประมวลผลคะแนน" />
        <Sidebar user={user} />
        <div className="content-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 114px)' }}>
          <div>
            <div className="spinner-border text-primary mb-3" role="status" style={{width: "3rem", height: "3rem"}}><span className="visually-hidden">Loading...</span></div>
            <div className="text-center">กำลังโหลดข้อมูล กรุณารอสักครู่...</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="wrapper">
      <Header user={user} title="รายงานประมวลผลคะแนน" />
      <Sidebar user={user} />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6"><h1 className="m-0">รายงานประมวลผลคะแนนภาพรวม</h1></div>
              <div className="col-sm-6"><ol className="breadcrumb float-sm-end"><li className="breadcrumb-item"><Link to="/super-admin/dashboard">หน้าหลัก</Link></li><li className="breadcrumb-item active">รายงานประมวลผลคะแนน</li></ol></div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {/* Action Card */}
            <div className="card">
              <div className="card-header bg-primary"><h3 className="card-title"><i className="fas fa-filter me-2"></i>เลือกปีงบประมาณและส่งออกรายงาน</h3></div>
              <div className="card-body">
                <div className="row align-items-end">
                  <div className="col-md-4">
                    <label htmlFor="fiscalYear" className="form-label">เลือกปีงบประมาณ</label>
                    <select id="fiscalYear" className="form-select" value={selectedFiscalYear} onChange={(e) => setSelectedFiscalYear(e.target.value)}>
                      <option value="">-- เลือกปีงบประมาณ --</option>
                      {fiscalYears.map(year => (<option key={year.id} value={year.id}>ปีงบประมาณ {year.year} {year.is_active && '(ปัจจุบัน)'}</option>))}
                    </select>
                  </div>
                  <div className="col-md-8">
                    <button className="btn btn-success me-2" onClick={exportToExcel} disabled={!selectedFiscalYear || !reportData.length}><i className="fas fa-file-excel me-2"></i>ส่งออก Excel</button>
                    <button className="btn btn-info" onClick={fetchReportData} disabled={!selectedFiscalYear || loading}>
                      {loading ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> กำลังโหลด</>) : (<><i className="fas fa-sync-alt me-2"></i>รีเฟรชข้อมูล</>)}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Boxes */}
            {summary && (
              <div className="row">
                <div className="col-lg-3 col-6"><div className="small-box bg-info"><div className="inner"><h3>{summary.total_schools}</h3><p>โรงเรียนทั้งหมด</p></div><div className="icon"><i className="fas fa-school"></i></div></div></div>
                <div className="col-lg-3 col-6"><div className="small-box bg-success"><div className="inner"><h3>{summary.passed_schools}</h3><p>ผ่านการประเมิน (≥75%)</p></div><div className="icon"><i className="fas fa-check-circle"></i></div></div></div>
                <div className="col-lg-3 col-6"><div className="small-box bg-warning"><div className="inner"><h3>{summary.need_improvement}</h3><p>ต้องปรับปรุง (65-74.99%)</p></div><div className="icon"><i className="fas fa-exclamation-triangle"></i></div></div></div>
                <div className="col-lg-3 col-6"><div className="small-box bg-danger"><div className="inner"><h3>{summary.urgent_improvement}</h3><p>ต้องปรับปรุงโดยด่วน ({'<'}65%)</p></div><div className="icon"><i className="fas fa-times-circle"></i></div></div></div>
              </div>
            )}

            {/* Main Report Table */}
            {selectedFiscalYear && (
              <div className="card">
                <div className="card-header bg-secondary"><h3 className="card-title"><i className="fas fa-table me-2"></i>รายงานประมวลผลคะแนน ITA {fiscalYears.find(fy => fy.id.toString() === selectedFiscalYear)?.year && ` ปีงบประมาณ ${fiscalYears.find(fy => fy.id.toString() === selectedFiscalYear).year}`}</h3></div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered table-hover mb-0" style={{fontSize: '0.8rem'}}>
                      <thead className="table-dark text-center align-middle">
                        <tr>
                          <th rowSpan="3">ลำดับ</th>
                          <th rowSpan="3">รหัส</th>
                          <th rowSpan="3" style={{minWidth: '200px'}}>ชื่อโรงเรียน</th>
                          <th colSpan="6">IIT (30)</th>
                          <th colSpan="4">EIT1 (15)</th>
                          <th colSpan="4">EIT2 (15)</th>
                          <th colSpan="8">OIT (40)</th>
                          <th rowSpan="3" className="bg-success">รวม<br/>(100)</th>
                          <th rowSpan="3" style={{minWidth: '120px'}}>ระดับ</th>
                        </tr>
                        <tr>
                          <th colSpan="5">ตัวชี้วัด (%)</th>
                          <th rowSpan="2">รวม<br/>IIT</th>
                          <th colSpan="3">ตัวชี้วัด (%)</th>
                          <th rowSpan="2">รวม<br/>EIT1</th>
                          <th colSpan="3">ตัวชี้วัด (%)</th>
                          <th rowSpan="2">รวม<br/>EIT2</th>
                          <th colSpan="5">9. การเปิดเผยข้อมูล (%)</th>
                          <th colSpan="2">10. การป้องกันการทุจริต (%)</th>
                          <th rowSpan="2">รวม<br/>OIT</th>
                        </tr>
                        <tr>
                          <th>T1</th><th>T2</th><th>T3</th><th>T4</th><th>T5</th>
                          <th>T6</th><th>T7</th><th>T8</th>
                          <th>T6</th><th>T7</th><th>T8</th>
                          <th>9.1</th><th>9.2</th><th>9.3</th><th>9.4</th><th>9.5</th>
                          <th>10.1</th><th>10.2</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.length > 0 ? (
                          reportData.map((school, index) => (
                            <tr key={school.school_id}>
                              <td className="text-center">{index + 1}</td>
                              <td className="text-center">{school.school_code}</td>
                              <td>{school.school_name}</td>
                              
                              {/* IIT Scores (%) */}
                              <td className="text-center">{school.iit_scores?.indicator_1 ? (school.iit_scores.indicator_1 / 6 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.iit_scores?.indicator_2 ? (school.iit_scores.indicator_2 / 6 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.iit_scores?.indicator_3 ? (school.iit_scores.indicator_3 / 6 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.iit_scores?.indicator_4 ? (school.iit_scores.indicator_4 / 6 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.iit_scores?.indicator_5 ? (school.iit_scores.indicator_5 / 6 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center bg-light"><strong>{school.iit_score?.toFixed(2) || '0.00'}</strong></td>

                              {/* EIT1 Scores (%) */}
                              <td className="text-center">{school.eit1_scores?.indicator_6 ? (school.eit1_scores.indicator_6 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.eit1_scores?.indicator_7 ? (school.eit1_scores.indicator_7 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.eit1_scores?.indicator_8 ? (school.eit1_scores.indicator_8 / 7 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center bg-light"><strong>{school.eit1_score?.toFixed(2) || '0.00'}</strong></td>
                              
                              {/* EIT2 Scores (%) */}
                              <td className="text-center">{school.eit2_scores?.indicator_6 ? (school.eit2_scores.indicator_6 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.eit2_scores?.indicator_7 ? (school.eit2_scores.indicator_7 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.eit2_scores?.indicator_8 ? (school.eit2_scores.indicator_8 / 7 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center bg-light"><strong>{school.eit2_score?.toFixed(2) || '0.00'}</strong></td>
                              
                              {/* OIT Sub-scores (%) */}
                              <td className="text-center">{school.oit_sub_scores?.s9_1 ? (school.oit_sub_scores.s9_1 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.oit_sub_scores?.s9_2 ? (school.oit_sub_scores.s9_2 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.oit_sub_scores?.s9_3 ? (school.oit_sub_scores.s9_3 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.oit_sub_scores?.s9_4 ? (school.oit_sub_scores.s9_4 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.oit_sub_scores?.s9_5 ? (school.oit_sub_scores.s9_5 / 4 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.oit_sub_scores?.s10_1 ? (school.oit_sub_scores.s10_1 / 10 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center">{school.oit_sub_scores?.s10_2 ? (school.oit_sub_scores.s10_2 / 10 * 100).toFixed(2) : '0.00'}</td>
                              <td className="text-center bg-light"><strong>{school.oit_score?.toFixed(2) || '0.00'}</strong></td>
                              
                              {/* Total & Rating */}
                              <td className="text-center bg-success text-white"><strong>{school.total_score?.toFixed(2) || '0.00'}</strong></td>
                              <td className="text-center"><span className={`badge ${getRatingBadgeClass(school.rating)} p-2 w-100`}>{school.rating || 'ไม่ระบุ'}</span></td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="23" className="text-center text-muted py-4">{selectedFiscalYear ? 'ไม่พบข้อมูลรายงาน' : 'กรุณาเลือกปีงบประมาณ'}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Description Card */}
            <div className="card">
              <div className="card-header bg-info"><h3 className="card-title"><i className="fas fa-info-circle me-2"></i>คำอธิบายตัวชี้วัด (คะแนนเต็มในวงเล็บ)</h3></div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 col-lg-3"><h5 className="text-primary">IIT (30)</h5><ul className="list-unstyled small"><li><strong>T1:</strong> การปฏิบัติหน้าที่ (6)</li><li><strong>T2:</strong> การใช้งบประมาณ (6)</li><li><strong>T3:</strong> การใช้อำนาจ (6)</li><li><strong>T4:</strong> การใช้ทรัพย์สินของทางราชการ (6)</li><li><strong>T5:</strong> การแก้ไขปัญหาการทุจริต (6)</li></ul></div>
                  <div className="col-md-6 col-lg-3"><h5 className="text-info">EIT (15+15)</h5><ul className="list-unstyled small"><li><strong>T6:</strong> คุณภาพการดำเนินงาน (4)</li><li><strong>T7:</strong> ประสิทธิภาพการสื่อสาร (4)</li><li><strong>T8:</strong> การปรับปรุงการทำงาน (7)</li></ul></div>
                  <div className="col-md-6 col-lg-3"><h5 className="text-warning">OIT (40)</h5><ul className="list-unstyled small"><li><strong>9.1:</strong> ข้อมูลพื้นฐาน (4)</li><li><strong>9.2:</strong> การบริหารงาน (4)</li><li><strong>9.3:</strong> การจัดซื้อจัดจ้าง (4)</li><li><strong>9.4:</strong> การบริหารและพัฒนาทรัพยากรบุคคล (4)</li><li><strong>9.5:</strong> การส่งเสริมความโปร่งใส (4)</li><li><strong>10.1:</strong> การดำเนินการเพื่อป้องกันการทุจริต (10)</li><li><strong>10.2:</strong> มาตรการภายในเพื่อป้องกันการทุจริต (10)</li></ul></div>
                  <div className="col-md-6 col-lg-3"><h5 className="text-success">ระดับการประเมิน</h5><ul className="list-unstyled small"><li><span className="badge bg-success">ผ่านดีเยี่ยม</span> ≥95%</li><li><span className="badge bg-info">ผ่านดี</span> 85 - 94.99%</li><li><span className="badge bg-primary">ผ่าน</span> 75 - 84.99%</li><li><span className="badge bg-warning">ต้องปรับปรุง</span> 65 - 74.99%</li><li><span className="badge bg-danger">ต้องปรับปรุงโดยด่วน</span> {'<'}65%</li></ul></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default ScoreSummaryReport;